import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { BillingService, BrandService, type BrandListItem, type CreditsResponse, type InvoiceRead } from '../client'
import { useAuth } from '../providers/auth/useAuth'
import { apiErrorMessage } from '../lib/api'
import { formatDate } from '../lib/format'
import { TIER_NAMES } from '../lib/plans'
import posthog from '../lib/posthog'
import Spinner from '../components/chat/Spinner'
import AddBrandDialog from '../components/brand/AddBrandDialog'
import Modal from '../components/ui/Modal'

const BUTTON = 'inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium'

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-stone-200 py-8 first:border-t-0 first:pt-0">
      <h2 className="font-heading text-lg font-semibold text-stone-900">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  )
}

export default function Billing() {
  const { account, brand, refreshAccount } = useAuth()
  const { pathname } = useLocation()
  const returned = pathname.endsWith('/success') ? 'success' : pathname.endsWith('/cancel') ? 'cancel' : null
  const brandId = brand?.id
  const subscription = account?.subscription ?? 'none'
  const active = subscription !== 'none'

  const [credits, setCredits] = useState<CreditsResponse | null>(null)
  const [invoices, setInvoices] = useState<InvoiceRead[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState<BrandListItem | null>(null)
  const brands = account?.brands ?? []

  useEffect(() => {
    if (returned === 'success') void refreshAccount()
  }, [returned, refreshAccount])

  useEffect(() => {
    if (!brandId) return

    BillingService.getCredits(brandId)
      .then(setCredits)
      .catch(() => undefined)
      .finally(() => setLoading(false))

    BillingService.getInvoices(brandId)
      .then(setInvoices)
      .catch(() => undefined)
  }, [brandId])

  function openPortal() {
    if (!brandId) return
    setBusy(true)
    setError(null)
    BillingService.portal({ brand_id: brandId })
      .then(({ url }) => {
        posthog.capture('billing_portal_opened')
        window.location.assign(url)
      })
      .catch((err: unknown) => {
        setError(apiErrorMessage(err))
        setBusy(false)
      })
  }

  async function deleteBrand(target: BrandListItem) {
    setBusy(true)
    setError(null)
    try {
      await BrandService.archiveBrand(target.id)
      await refreshAccount()
      setDeleting(null)
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const usedPercent = credits ? Math.min(100, Math.round((credits.credits_used / Math.max(credits.credits_total, 1)) * 100)) : 0

  return (
    <div className="flex-1 overflow-y-auto px-4 py-8 md:px-6">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Billing &amp; invoices</h1>

        {returned === 'success' && (
          <p className="mt-6 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            Payment confirmed. Your credits appear here once Stripe finishes; refresh if they are not showing yet.
          </p>
        )}

        {returned === 'cancel' && (
          <p className="mt-6 rounded-lg bg-stone-200 px-4 py-3 text-sm text-stone-600">
            Checkout cancelled. Nothing was charged.
          </p>
        )}

        {error && <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

        <div className="mt-8">
          <Section title="Current plan">
            {loading ? (
              <Spinner className="size-4 border-stone-300 border-t-stone-500" />
            ) : active ? (
              <div className="rounded-2xl border border-stone-200 bg-stone-100 p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading text-xl font-semibold text-stone-900">{TIER_NAMES[subscription]}</p>
                    <p className="mt-1 text-sm text-stone-500">Renews monthly. Billed through Stripe.</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={openPortal}
                    className={`${BUTTON} border border-stone-300 bg-stone-100 text-stone-900 transition-colors enabled:hover:bg-stone-200 disabled:opacity-60`}
                  >
                    Manage billing
                  </button>
                </div>

                {credits && (
                  <div className="mt-6">
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-stone-500">Ad credits used</span>
                      <span className="font-medium text-stone-900">
                        {credits.credits_used.toLocaleString()} / {credits.credits_total.toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200">
                      <div className="h-full rounded-full bg-purple-700 transition-[width]" style={{ width: `${usedPercent}%` }} />
                    </div>
                    <p className="mt-2 text-xs text-stone-400">
                      {credits.remaining.toLocaleString()} credits remaining
                      {credits.topup_credits > 0 &&
                        ` — ${credits.topup_credits.toLocaleString()} of it topped up`}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-100 p-6 text-center">
                <p className="text-sm text-stone-500">You don&rsquo;t have an active subscription.</p>
                <Link
                  to="/plans"
                  className={`${BUTTON} mt-4 bg-linear-to-b from-stone-700 to-stone-950 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] transition-opacity hover:opacity-90`}
                >
                  View plans
                </Link>
              </div>
            )}
          </Section>

          <Section title="Brands">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-stone-500">
                {brands.length} of {account?.brand_limit ?? 1} brands on your plan. They share one credit balance.
              </p>
              <button
                type="button"
                onClick={() => setAdding(true)}
                className={`${BUTTON} border border-stone-300 bg-stone-100 text-stone-900 transition-colors hover:bg-stone-200`}
              >
                Add brand
              </button>
            </div>
            <ul className="mt-4 divide-y divide-stone-200 rounded-2xl border border-stone-200 bg-stone-100">
              {brands.map((item) => (
                <li key={item.id} className="flex items-center gap-4 px-5 py-3.5 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-stone-900">{item.name}</span>
                    {item.website && <span className="block truncate text-xs text-stone-400">{item.website}</span>}
                  </span>
                  {item.locked && (
                    <Link to="/plans" className="shrink-0 rounded-full bg-stone-200 px-2.5 py-1 text-xs text-stone-500">
                      Locked · upgrade
                    </Link>
                  )}
                  <button
                    type="button"
                    disabled={brands.length <= 1}
                    onClick={() => setDeleting(item)}
                    className="shrink-0 text-xs text-red-600 transition-opacity enabled:hover:underline disabled:opacity-40"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Invoices">
            {invoices.length === 0 ? (
              <p className="text-sm text-stone-500">
                No invoices yet. They appear here once you start a subscription.
              </p>
            ) : (
              <ul className="divide-y divide-stone-200 rounded-2xl border border-stone-200 bg-stone-100">
                {invoices.map((invoice) => (
                  <li key={invoice.id} className="flex items-center gap-4 px-5 py-3.5 text-sm">
                    <span className="w-28 shrink-0 text-stone-500">{formatDate(invoice.created)}</span>
                    <span className="min-w-0 flex-1 truncate text-stone-900">{invoice.number ?? invoice.id}</span>
                    <span className="shrink-0 font-medium text-stone-900">
                      ${(invoice.amount_paid / 100).toLocaleString()}
                    </span>
                    <span
                      className={`w-20 shrink-0 text-right text-xs ${
                        invoice.status === 'paid' ? 'text-emerald-600' : 'text-stone-400'
                      }`}
                    >
                      {invoice.status}
                    </span>
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-stone-500 underline underline-offset-2 hover:text-stone-900"
                      >
                        View
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {active && (
            <Section title="Cancel subscription">
              <p className="max-w-xl text-sm text-stone-500">
                Cancelling stops the next renewal. You keep your plan and any remaining credits until the end of the
                current billing period.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={openPortal}
                className={`${BUTTON} mt-4 border border-red-200 bg-stone-100 text-red-600 transition-colors enabled:hover:bg-red-100 disabled:opacity-60`}
              >
                Cancel subscription
              </button>
            </Section>
          )}
        </div>
      </div>

      {adding && <AddBrandDialog onClose={() => setAdding(false)} />}

      {deleting && (
        <Modal title={`Delete ${deleting.name}?`} width="max-w-sm" onClose={() => setDeleting(null)}>
          <p className="text-sm text-stone-600">
            Are you sure you want to delete this brand? This can&rsquo;t be undone. Its chats, ads, competitors and
            reference images will no longer be available.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDeleting(null)}
              className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void deleteBrand(deleting)}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
            >
              {busy ? 'Deleting…' : 'Delete brand'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
